import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, X } from "lucide-react";
import clsx from "clsx";
import { ApiError } from "@/api/client";
import * as interestsApi from "@/api/interests";
import type { InterestType, RefInterest } from "@/api/types";

interface ProposeInterestInputProps {
  /** Interest type the new proposal lands under (e.g. `sport_modern`, `touchstone_film`). */
  interestType: InterestType;
  /** Optional category filter for live suggestions + categoryHint sent to the admin queue. */
  category?: string;
  /** Codes already selected by the user — filtered out of suggestions. */
  excludeCodes?: string[];
  placeholder?: string;
  /**
   * Called when the user accepts a suggestion or successfully proposes a new one.
   * Parent decides how to integrate the resulting RefInterest (typically: add to selections).
   */
  onAdd: (interest: RefInterest, status: "matched" | "created_pending" | "created_approved") => void;
}

// Tiny inline debounce — no library dependency.
function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

type Feedback =
  | { kind: "success"; msg: string }
  | { kind: "pending"; msg: string }
  | { kind: "error"; msg: string };

export default function ProposeInterestInput({
  interestType,
  category,
  excludeCodes = [],
  placeholder = "Add your own…",
  onAdd,
}: ProposeInterestInputProps) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const debouncedQuery = useDebouncedValue(query.trim(), 200);
  const inputRef = useRef<HTMLInputElement>(null);

  // Live suggestions kick in at 2+ chars so we don't blast the API on a single keystroke.
  const { data: searchData } = useQuery({
    queryKey: ["interests-search", debouncedQuery, category],
    queryFn: () =>
      interestsApi.searchInterests({
        q: debouncedQuery,
        category,
        limit: 6,
      }),
    enabled: debouncedQuery.length >= 2,
    staleTime: 30_000,
  });

  const excludeSet = new Set(excludeCodes);
  const suggestions = (searchData?.data?.items ?? []).filter(
    (i) => !excludeSet.has(i.code),
  );

  const proposeMutation = useMutation({
    mutationFn: () =>
      interestsApi.proposeInterest({
        type: interestType,
        label: query.trim(),
        ...(category && { categoryHint: category }),
      }),
    onSuccess: (response) => {
      const data = response.data;
      // Lift the propose response into a RefInterest the parent can drop into
      // its selections immediately. Some shape fields aren't returned by the
      // propose endpoint; defaults are safe because they're either visual
      // (emoji, displayNames) or unused for matching while pending.
      const interest: RefInterest = {
        id: data.interest.id,
        code: data.interest.code,
        canonicalNameEn: data.interest.canonicalNameEn,
        interestType: data.interest.interestType,
        category: data.interest.category,
        subcategory: null,
        emoji: data.interest.emoji,
        displayNames: {},
        stateNames: {},
        searchAliases: [],
        region: null,
        isPanIndia: false,
        isPopular: false,
        popularInStates: [],
        eraRelevance: [],
        sortOrder: 999,
        status: data.interest.status,
      };
      onAdd(interest, data.status);

      if (data.status === "matched") {
        setFeedback({
          kind: "success",
          msg: `Added "${interest.canonicalNameEn}".`,
        });
      } else if (data.status === "created_approved") {
        setFeedback({
          kind: "success",
          msg: `Added "${interest.canonicalNameEn}" — others have been asking for this too!`,
        });
      } else {
        setFeedback({
          kind: "pending",
          msg: `Submitted "${interest.canonicalNameEn}" for review. You can use it now; it'll go public once we review.`,
        });
      }

      setQuery("");
      // Refresh the user's pending list so the "review" badge appears on
      // the new pill without a hard refresh.
      queryClient.invalidateQueries({ queryKey: ["my-proposals"] });
      queryClient.invalidateQueries({ queryKey: ["interests"] });
    },
    onError: (err: unknown) => {
      let msg = "Could not submit. Please try again.";
      if (err instanceof ApiError) {
        // Surface the friendly server-side message (profanity gate, throttle, etc.).
        msg = err.message;
      }
      setFeedback({ kind: "error", msg });
    },
  });

  const handlePickSuggestion = (interest: RefInterest) => {
    onAdd(interest, "matched");
    setFeedback({
      kind: "success",
      msg: `Added "${interest.canonicalNameEn}".`,
    });
    setQuery("");
  };

  const handleSubmit = () => {
    if (!query.trim() || proposeMutation.isPending) return;
    proposeMutation.mutate();
  };

  const handleClose = () => {
    setOpen(false);
    setQuery("");
    setFeedback(null);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => {
          setOpen(true);
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm border border-dashed border-gray-300 text-gray-500 hover:border-primary-400 hover:text-primary-700 transition-colors"
      >
        <Plus className="w-3.5 h-3.5" /> Add your own
      </button>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setFeedback(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSubmit();
            if (e.key === "Escape") handleClose();
          }}
          placeholder={placeholder}
          className="input flex-1"
          maxLength={100}
        />
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!query.trim() || proposeMutation.isPending}
          className="btn-primary text-sm px-3 py-1.5 disabled:opacity-50"
        >
          {proposeMutation.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            "Add"
          )}
        </button>
        <button
          type="button"
          onClick={handleClose}
          className="text-gray-400 hover:text-gray-600"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Live suggestions — clicking either picks the canonical row directly. */}
      {suggestions.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {suggestions.map((s) => (
            <button
              key={s.code}
              type="button"
              onClick={() => handlePickSuggestion(s)}
              className="px-2.5 py-1 rounded-full text-xs border border-gray-200 text-gray-700 hover:border-primary-400 hover:bg-primary-50"
            >
              {s.emoji && <span className="mr-1">{s.emoji}</span>}
              {s.canonicalNameEn}
            </button>
          ))}
        </div>
      )}

      {feedback && (
        <p
          className={clsx(
            "text-xs",
            feedback.kind === "error" && "text-red-600",
            feedback.kind === "pending" && "text-amber-700",
            feedback.kind === "success" && "text-twins-dark",
          )}
        >
          {feedback.msg}
        </p>
      )}
    </div>
  );
}

